import { useMemo, useState } from "react";
import { Calendar, Plus } from "lucide-react";
import { useAppointments, useSlotBlocks, usePublicAppSettings, useEffectiveSlotConfig, type SlotBlock, type AppointmentRow as ApptRow } from "@/lib/queries";
import { useReorderAppointment, useDayReorderAppointment } from "@/lib/mutations";
import { AppointmentRow } from "@/features/appointments/AppointmentRow";
import { PendingConfirmationsPanel } from "@/features/appointments/PendingConfirmationsPanel";
import { BookDialog } from "@/features/appointments/BookDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Has } from "@/components/Has";
import { isSlotBlocked, calendarDayStatus } from "@emr/shared/scheduling";
import type { WeeklySchedule, SlotDef } from "@emr/shared/scheduling";
import { fmtDate, todayIST, dateIST } from "@emr/shared/india";
import { cn } from "@/lib/utils";

function DayPicker({ date, onDateChange, blocks, weeklySchedule }: { date: string; onDateChange: (d: string) => void; blocks: SlotBlock[]; weeklySchedule?: WeeklySchedule }) {
  const [view, setView] = useState(() => { const d = new Date(date + "T00:00:00"); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const days = useMemo(() => {
    const y = view.getFullYear();
    const m = view.getMonth(); // 0-based
    const first = new Date(y, m, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const last = new Date(y, m + 1, 0).getDate(); // # of days in this month
    const out: { date?: string; day?: number }[] = [];
    // Leading blanks (days of the previous month that fall in the first week).
    for (let i = 0; i < startOffset; i++) out.push({});
    // Build a YYYY-MM-DD key from LOCAL components — never toISOString(), which
    // converts to UTC and shifts IST (UTC+5:30) dates back by a day.
    for (let d = 1; d <= last; d++) {
      const ds = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      out.push({ date: ds, day: d });
    }
    // Trailing blanks so the grid is a whole number of weeks and the last
    // days don't wrap into the next week's row.
    while (out.length % 7 !== 0) out.push({});
    return out;
  }, [view]);

  const today = todayIST();
  const maxDate = (() => { const d = new Date(); d.setMonth(d.getMonth() + 6); return dateIST(d); })();

  return (
    <div className="w-64 shrink-0">
      <div className="mb-2 flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}>‹</Button>
        <span className="text-sm font-medium">{view.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</span>
        <Button size="sm" variant="ghost" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}>›</Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d.date) return <div key={i} />;
          const dObj = new Date(d.date + "T00:00:00");
          // Calendar status covers ALL no-OPD cases: off weekdays (weekly
          // schedule — e.g. Sunday off in Slot Config) AND whole-day blocks.
          // (2026-10-07: previously only slot_blocks overrides were shown, so
          // Sundays looked like normal OPD days.)
          const status = calendarDayStatus(dObj, weeklySchedule as WeeklySchedule, blocks as any);
          const isSelected = d.date === date;
          const isPast = d.date < today;
          const isFuture = d.date > maxDate;
          const closed = status.kind === "off" || status.kind === "blocked";
          const title =
            status.kind === "off" ? "Clinic closed (weekly schedule — Settings → Slot Config)"
            : status.kind === "blocked" ? `Fully blocked${status.reason ? ` — ${status.reason}` : ""} (Settings → Block Days)`
            : status.kind === "partial" ? `Partial override${status.reason ? ` — ${status.reason}` : ""}`
            : undefined;
          return (
            <button
              key={i}
              disabled={isFuture}
              onClick={() => onDateChange(d.date!)}
              title={title}
              className={cn(
                "relative aspect-square rounded-lg text-sm transition",
                isSelected
                  ? "bg-maroon-600 text-cream-50"
                  : status.kind === "off"
                    ? "bg-slate-200 text-slate-500 dark:bg-dark-700 dark:text-dark-300"
                    : status.kind === "blocked"
                      ? "bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-100"
                      : status.kind === "partial"
                        ? "bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100"
                        : isPast ? "text-muted/40" : "hover:bg-cream-100 dark:hover:bg-dark-700",
              )}
            >
              {d.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QueueList({ slotKey, appts, date, blocks, weeklySchedule }: { slotKey: string; appts: ApptRow[]; date: string; blocks: any[]; weeklySchedule?: WeeklySchedule }) {
  const reorder = useReorderAppointment();
  const dayReorder = useDayReorderAppointment();
  const dObj = new Date(date + "T00:00:00");
  // Day-level status first: off weekdays and whole-day blocks render a
  // "closed" banner instead of the misleading "No appointments".
  const dayStatus = calendarDayStatus(dObj, weeklySchedule as WeeklySchedule, blocks as any);
  if (dayStatus.kind === "off") {
    return (
      <div className="rounded-lg bg-slate-100 p-4 text-sm text-slate-600 dark:bg-dark-700 dark:text-dark-200">
        Clinic closed — {dObj.toLocaleDateString("en-IN", { weekday: "long" })} is a non-working day (Settings → Slot Config).
      </div>
    );
  }
  if (dayStatus.kind === "blocked") {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">Day blocked (Settings → Block Days)</div>
    );
  }
  // Per-slot blocked banner — generic key; the reason is admin-only (the API
  // nulls it for non-admins) and is never rendered on this page.
  if (isSlotBlocked(dObj, slotKey, blocks as any)) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">Slot blocked (Settings → Block Days)</div>
    );
  }

  if (appts.length === 0) {
    return <div className="p-4 text-center text-sm text-muted">No appointments</div>;
  }

  return (
    <div>
      {appts.map((a) => (
        <AppointmentRow
          key={a.id}
          appt={a}
          daySerial={a.daySerial}
          onReorder={(id, dir) => {
            if (a.status === "CheckedIn") dayReorder.mutate({ id, direction: dir });
            else reorder.mutate({ id, direction: dir });
          }}
        />
      ))}
    </div>
  );
}

export default function PendingAppointments() {
  const today = todayIST();
  const [date, setDate] = useState(today);
  const { data, isLoading } = useAppointments(date);
  const { data: blocksData } = useSlotBlocks();
  const { data: settingsData } = usePublicAppSettings();
  const blocks = blocksData?.rows ?? [];
  const weeklySchedule = settingsData?.weeklySchedule as WeeklySchedule | undefined;
  const [bookOpen, setBookOpen] = useState(false);
  // The selected date's effective config (legacy Morning/Evening fallback).
  const { slotList } = useEffectiveSlotConfig(date, settingsData);

  const rows = data?.rows ?? [];
  // Confirmed appointments for the selected date: Booked + CheckedIn + Completed.
  // Pending appointments are shown in the PendingConfirmationsPanel above.
  // This section shows the full confirmed queue with check-in/vitals/cancel actions.
  const confirmed = useMemo(
    () => rows.filter((r) => r.status === "Booked" || r.status === "CheckedIn" || r.status === "Completed"),
    [rows],
  );
  // Group per config slot key; drift rows (slot not in the config) fall into
  // a trailing "Unassigned" accordion, only when non-empty.
  const { bySlot, unassigned } = useMemo(() => {
    const map = new Map<string, ApptRow[]>();
    for (const def of slotList) map.set(def.key, []);
    const drift: ApptRow[] = [];
    for (const r of confirmed) {
      const arr = map.get(r.slot ?? "");
      if (arr) arr.push(r);
      else drift.push(r);
    }
    return { bySlot: map, unassigned: drift };
  }, [confirmed, slotList]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Pending Appointments</h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-muted"><Calendar size={16} /> {fmtDate(date)}</div>
          <Has perm="appointments.create">
            <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => setBookOpen(true)}>
              <Plus size={14} className="mr-1" /> Book appointment
            </Button>
          </Has>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        <Card className="w-fit shrink-0 p-4">
          <DayPicker date={date} onDateChange={setDate} blocks={blocks} weeklySchedule={weeklySchedule} />
        </Card>

        <div className="min-w-0 flex-1">
          <PendingConfirmationsPanel date={date} />
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-maroon-700 dark:text-amber-500">Confirmed appointments — {fmtDate(date)}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <Accordion type="multiple" defaultValue={slotList.map((s) => s.key)} className="w-full">
              {slotList.map((def) => (
                <AccordionItem key={def.key} value={def.key} className="border-cream-200 dark:border-dark-700">
                  <AccordionTrigger className="px-4 text-maroon-700 dark:text-amber-500 hover:no-underline">
                    <span className="flex items-center gap-2">
                      {def.label} ({bySlot.get(def.key)?.length ?? 0})
                      <span className="text-xs font-normal text-muted">{def.startTime}–{def.endTime}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="p-0">
                    <QueueList slotKey={def.key} appts={bySlot.get(def.key) ?? []} date={date} blocks={blocks} weeklySchedule={weeklySchedule} />
                  </AccordionContent>
                </AccordionItem>
              ))}
              {unassigned.length > 0 && (
                <AccordionItem value="__unassigned" className="border-cream-200 dark:border-dark-700">
                  <AccordionTrigger className="px-4 text-maroon-700 dark:text-amber-500 hover:no-underline">
                    <span className="flex items-center gap-2">Unassigned ({unassigned.length})</span>
                  </AccordionTrigger>
                  <AccordionContent className="p-0">
                    <QueueList slotKey="" appts={unassigned} date={date} blocks={blocks} weeklySchedule={weeklySchedule} />
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          )}
        </CardContent>
      </Card>

      <BookDialog open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}
